import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar,
  faCogs,
  faExclamationTriangle,
  faBolt,
  faSearch,
  faSatellite,
  faDatabase,
  faLeaf,
  faFileCode,
  faGlobe,
  faFolderOpen,
  faRadio,
  faEye,
  faStream,
  faPlay,
  faCheckCircle,
  faTimesCircle,
  faSpinner,
  faChevronRight,
  faHome,
  faInfoCircle,
  faTimes,
  faExternalLinkAlt,
  faChartPie,
  faDesktop,
  faServer,
  faNetworkWired,
  faHdd,
  faClock,
  faRobot
} from '@fortawesome/free-solid-svg-icons';
import apiService from '../services/api';

const CompactOverview = ({ onNavigateToLogs }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [showAIModal, setShowAIModal] = useState(false);
  const [showAIPromoPopup, setShowAIPromoPopup] = useState(false);
  const [aiText, setAiText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [liveData, setLiveData] = useState({
    activePipelines: 127,
    eoTools: 20,
    monitoringLayers: 45,
    dataSources: 67,
    currentTime: new Date(),
    processingSpeed: 1.24,
    dataTransfer: 234.56,
    activeUsers: 12,
    systemLoad: 67.8,
    problems: 7,
    availability: 37,
    smartscape: 37,
    services: {
      sentinel: 58,
      landsat: 19,
      modis: 23,
      copernicus: 31
    },
    eoInstances: {
      total: 210,
      processing: 6
    },
    satellites: 873,
    eoContainers: 122,
    algorithms: 34,
    geoDatabase: {
      queries: 290,
      geoprocessing: 1.81,
      responseTime: 5
    },
    spatialProcesses: 63,
    geoservers: 17,
    dataVolume: 131
  });

  const [loading, setLoading] = useState(true);
  const [realtimeAlerts, setRealtimeAlerts] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [hoveredSegment, setHoveredSegment] = useState(null);

  // Chart data
  const servicesChartData = [
    { time: '07:00', sentinel: 45, landsat: 15, modis: 20, copernicus: 28 },
    { time: '07:30', sentinel: 52, landsat: 18, modis: 22, copernicus: 30 },
    { time: '08:00', sentinel: 48, landsat: 16, modis: 19, copernicus: 29 },
    { time: '08:30', sentinel: 55, landsat: 20, modis: 25, copernicus: 32 },
    { time: '09:00', sentinel: 58, landsat: 19, modis: 23, copernicus: 31 }
  ];

  const networkChartData = Array.from({ length: 50 }, (_, i) => ({
    time: i,
    value: 80 + Math.random() * 40
  }));

  const pieData = [
    { name: 'Sentinel-2', value: 85.3, color: '#8b5cf6' },
    { name: 'Landsat-8/9', value: 59.8, color: '#6366f1' },
    { name: 'MODIS', value: 46.7, color: '#3b82f6' },
    { name: 'Copernicus', value: 27.2, color: '#06b6d4' },
    { name: 'Planet Labs', value: 18.5, color: '#10b981' }
  ];

  const availabilityData = Array.from({ length: 37 }, (_, i) => ({
    id: i,
    status: Math.random() > 0.1 ? 'healthy' : 'warning'
  }));

  useEffect(() => {
    const loadData = async () => {
      try {
        const [alertsData, dashboardMetrics] = await Promise.all([
          apiService.getRecentAlerts(),
          apiService.getDashboardMetrics()
        ]);
        setRealtimeAlerts(alertsData);
        setLiveData(dashboardMetrics);
        setLoading(false);

        // Simulate data loading delay
        setTimeout(() => {
          setIsLoading(false);
        }, 1500);

        // Check if AI Assistant promo has been shown before
        const aiPromoShown = localStorage.getItem('spheroid_ai_promo_shown');
        if (!aiPromoShown) {
          setTimeout(() => {
            setShowAIPromoPopup(true);
            localStorage.setItem('spheroid_ai_promo_shown', 'true');
          }, 2000);
        }
      } catch (error) {
        console.error('Failed to load data:', error);
        setLoading(false);
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Simulate real-time data updates
  useEffect(() => {
    const interval = setInterval(() => {
      setLiveData(prev => ({
        ...prev,
        activePipelines: prev.activePipelines + Math.floor(Math.random() * 6) - 2,
        dataSources: Math.max(60, prev.dataSources + Math.floor(Math.random() * 4) - 1),
        currentTime: new Date(),
        processingSpeed: Math.max(0.8, prev.processingSpeed + (Math.random() * 0.4) - 0.2),
        dataTransfer: Math.max(200, prev.dataTransfer + (Math.random() * 40) - 20),
        activeUsers: Math.max(8, prev.activeUsers + Math.floor(Math.random() * 6) - 2),
        systemLoad: Math.max(45, Math.min(95, prev.systemLoad + (Math.random() * 10) - 5)),
        dockerContainers: Math.max(100, prev.dockerContainers + Math.floor(Math.random() * 10) - 5),
        networkVolume: Math.max(100, prev.networkVolume + Math.floor(Math.random() * 20) - 10)
      }));
      setLastUpdate(new Date());
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  // AI Summary streaming from Ollama with word-by-word display
  const handleAIClick = async () => {
    setShowAIPromoPopup(false);
    setShowAIModal(true);
    setAiText('🔍 Analyzing system metrics...\n\nThis will take a few seconds. Please wait.');
    setIsStreaming(true);

    try {
      const API_BASE_URL = (window.ENV && window.ENV.API_URL) || 'https://spheroid.scaleapp.tech/api';
      const response = await fetch(`${API_BASE_URL}/ai-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          metrics: {
            activePipelines: liveData.activePipelines,
            systemLoad: liveData.systemLoad,
            dataTransfer: liveData.dataTransfer,
            processingSpeed: liveData.processingSpeed,
            activeUsers: liveData.activeUsers,
            problems: liveData.problems,
            dockerContainers: liveData.dockerContainers,
            networkVolume: liveData.networkVolume
          }
        })
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let isFirstChunk = true;

      // Word-by-word streaming with delay
      const displayWordByWord = async (text) => {
        const words = text.split(/(\s+)/); // Split by whitespace but keep the spaces
        for (const word of words) {
          setAiText(prev => prev + word);
          await new Promise(resolve => setTimeout(resolve, 30)); // 30ms delay per word
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          if (buffer) {
            await displayWordByWord(buffer);
          }
          break;
        }

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.text) {
                // Clear loading message on first chunk
                if (isFirstChunk) {
                  setAiText('');
                  isFirstChunk = false;
                }
                buffer += data.text;
                // Display buffer when it has enough content (e.g., 50 chars or a sentence)
                if (buffer.length > 50 || buffer.includes('.') || buffer.includes('\n')) {
                  await displayWordByWord(buffer);
                  buffer = '';
                }
              }
              if (data.done) {
                if (buffer) {
                  await displayWordByWord(buffer);
                }
                setIsStreaming(false);
              }
              if (data.error) {
                setAiText(prev => prev + `\n\n❌ Error: ${data.error}`);
                setIsStreaming(false);
              }
            } catch (e) {
              console.error('Parse error:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('AI Analysis error:', error);
      setAiText(`❌ Failed to connect to AI analysis service.\n\nPlease ensure Ollama is running on localhost:11434`);
      setIsStreaming(false);
    }
  };

  if (loading) {
    return (
      <div className="compact-overview-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading system data...</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="compact-overview-container">
        <div className="dashboard-loading">
          <div className="loading-orbit">
            <div className="orbit-circle"></div>
            <div className="orbit-circle"></div>
            <div className="orbit-circle"></div>
          </div>
          <div className="loading-text">
            <h3>Loading Dashboard</h3>
            <p>Fetching real-time satellite data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="compact-overview-container">
      <div className="compact-overview-header overview-summary" style={{ position: 'relative' }}>
        <h2>
          <FontAwesomeIcon icon={faChartBar} style={{ marginRight: '0.5rem' }} />
          Dashboard
          <svg width="120" height="30" viewBox="0 0 120 30" style={{marginLeft: '1rem', verticalAlign: 'middle'}}>
            <polyline
              points="0,15 20,15 24,8 30,22 35,15 115,15"
              fill="none"
              stroke="#10b981"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="heartbeat-line"
            />
            <circle cx="118" cy="15" r="3" fill="#10b981" className="heartbeat-dot"/>
          </svg>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', position: 'relative' }}>
            <button
              className="ai-assistant-btn pulse-animation"
              onClick={handleAIClick}
              title="AI System Analysis"
            >
              <FontAwesomeIcon icon={faRobot} />
            </button>
            <span
              onClick={handleAIClick}
              style={{
                fontSize: '0.75rem',
                color: '#3b82f6',
                cursor: 'pointer',
                fontWeight: '500',
                animation: 'pulse 2s infinite',
                letterSpacing: '0.5px'
              }}
            >
              AI Insights ✨
            </span>

            {/* AI Assistant Promo Popup - positioned right next to the button */}
            {showAIPromoPopup && (
              <div
                style={{
                  position: 'absolute',
                  top: '45px',
                  left: '-10px',
                  zIndex: 10000,
                  animation: 'bounce 2s infinite'
                }}
              >
                <div
                  style={{
                    background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                    border: '2px solid #fb923c',
                    borderRadius: '12px',
                    padding: '1rem 1.25rem',
                    minWidth: '320px',
                    boxShadow: '0 10px 30px rgba(249, 115, 22, 0.4)',
                    position: 'relative',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {/* Arrow pointing up to AI button */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '-20px',
                      left: '30px',
                      width: '0',
                      height: '0',
                      borderLeft: '12px solid transparent',
                      borderRight: '12px solid transparent',
                      borderBottom: '20px solid #f97316'
                    }}
                  />

                  {/* Content */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        background: '#fff',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faRobot}
                        style={{
                          fontSize: '1.25rem',
                          color: '#f97316'
                        }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3
                        style={{
                          color: '#fff',
                          fontSize: '0.95rem',
                          fontWeight: 'bold',
                          marginBottom: '0.25rem'
                        }}
                      >
                        Earth Observation AI
                      </h3>
                      <p
                        style={{
                          color: '#fed7aa',
                          fontSize: '0.85rem',
                          lineHeight: '1.4',
                          marginBottom: '0'
                        }}
                      >
                        👆 Click here to get AI-powered insights!
                      </p>
                    </div>
                    <button
                      onClick={() => setShowAIPromoPopup(false)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#fed7aa',
                        fontSize: '1.25rem',
                        cursor: 'pointer',
                        padding: '0.25rem',
                        lineHeight: 1,
                        transition: 'color 0.2s',
                        flexShrink: 0
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#fff'}
                      onMouseLeave={(e) => e.target.style.color = '#fed7aa'}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </h2>
        <div className="live-indicator">
          <div className="live-dot live"></div>
          <span className="live-text">
            Last {lastUpdate.toLocaleTimeString().split(':').slice(0, 2).join(':')}
          </span>
        </div>
      </div>

      <div className="compact-grid">
        {/* My Pipelines - Full Width - FIRST POSITION */}
        <div className="compact-widget my-pipelines-widget" id="card-my-pipelines" style={{gridColumn: 'span 2'}} title="View detailed pipeline information">
          <div className="widget-header">
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%'}}>
              <div>
                <h3>My Pipelines</h3>
                <div className="widget-subtitle">Custom EO Processing Workflows</div>
              </div>
              <div style={{display: 'flex', gap: '1rem', fontSize: '0.75rem'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <div style={{width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981'}}></div>
                  <span>Running (2)</span>
                </div>
                <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <div style={{width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#3b82f6'}}></div>
                  <span>Completed (3)</span>
                </div>
                <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <div style={{width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#ef4444'}}></div>
                  <span>Failed (1)</span>
                </div>
              </div>
            </div>
          </div>
          <div className="widget-content">
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>

              {/* Pipeline 1: ECMWF → CDO Processing */}
              <div
                onClick={() => window.location.href = '/pipelines?id=ecmwf-cdo'}
                style={{
                  padding: '1rem',
                  background: 'rgba(16, 185, 129, 0.08)',
                  border: '1px solid rgba(16, 185, 129, 0.25)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.12)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.08)'}
              >
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <div style={{flex: 1}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                      <div>
                        <div style={{fontSize: '0.9rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.25rem'}}>
                          ECMWF Weather → CDO Processing
                        </div>
                        <div style={{fontSize: '0.7rem', color: '#888'}}>
                          ERA5 Reanalysis • 📥 Download → ⚙️ CDO → 📊 NetCDF
                        </div>
                      </div>
                      <div style={{padding: '0.25rem 0.6rem', background: 'rgba(16, 185, 129, 0.2)', border: '1px solid rgba(16, 185, 129, 0.4)', borderRadius: '4px', fontSize: '0.65rem', fontWeight: '600', color: '#10b981'}}>
                        RUNNING
                      </div>
                    </div>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', gap: '2rem', fontSize: '0.75rem', paddingLeft: '1.5rem'}}>
                    <div>
                      <span style={{color: '#888'}}>Progress: </span>
                      <span style={{fontWeight: '600', color: '#10b981'}}>67%</span>
                    </div>
                    <div>
                      <span style={{color: '#888'}}>ETA: </span>
                      <span style={{fontWeight: '600'}}>4 min</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pipeline 2: Sentinel-2 → Python Analytics */}
              <div
                onClick={() => window.location.href = '/pipelines?id=sentinel2-ndvi'}
                style={{
                  padding: '1rem',
                  background: 'rgba(16, 185, 129, 0.08)',
                  border: '1px solid rgba(16, 185, 129, 0.25)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.12)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.08)'}
              >
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <div style={{flex: 1}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                      <div>
                        <div style={{fontSize: '0.9rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.25rem'}}>
                          Sentinel-2 → NDVI Analytics
                        </div>
                        <div style={{fontSize: '0.7rem', color: '#888'}}>
                          Vegetation Monitoring • 📥 S2 L2A → 🐍 Python → 📊 GeoTIFF
                        </div>
                      </div>
                      <div style={{padding: '0.25rem 0.6rem', background: 'rgba(16, 185, 129, 0.2)', border: '1px solid rgba(16, 185, 129, 0.4)', borderRadius: '4px', fontSize: '0.65rem', fontWeight: '600', color: '#10b981'}}>
                        RUNNING
                      </div>
                    </div>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', gap: '2rem', fontSize: '0.75rem', paddingLeft: '1.5rem'}}>
                    <div>
                      <span style={{color: '#888'}}>Progress: </span>
                      <span style={{fontWeight: '600', color: '#10b981'}}>45%</span>
                    </div>
                    <div>
                      <span style={{color: '#888'}}>ETA: </span>
                      <span style={{fontWeight: '600'}}>8 min</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pipeline 3: MODIS → Time Series */}
              <div
                onClick={() => window.location.href = '/pipelines?id=modis-lst'}
                style={{
                  padding: '1rem',
                  background: 'rgba(59, 130, 246, 0.08)',
                  border: '1px solid rgba(59, 130, 246, 0.25)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.12)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.08)'}
              >
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <div style={{flex: 1}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                      <div>
                        <div style={{fontSize: '0.9rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.25rem'}}>
                          MODIS → LST Time Series
                        </div>
                        <div style={{fontSize: '0.7rem', color: '#888'}}>
                          Temperature Analysis • 📥 48 HDF → ⚙️ Interpolation → ✅ Mosaic
                        </div>
                      </div>
                      <div style={{padding: '0.25rem 0.6rem', background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.4)', borderRadius: '4px', fontSize: '0.65rem', fontWeight: '600', color: '#3b82f6'}}>
                        COMPLETED
                      </div>
                    </div>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', gap: '2rem', fontSize: '0.75rem', paddingLeft: '1.5rem'}}>
                    <div>
                      <span style={{color: '#888'}}>Runtime: </span>
                      <span style={{fontWeight: '600'}}>12 min</span>
                    </div>
                    <div style={{color: '#10b981', fontWeight: '600'}}>Success ✓</div>
                  </div>
                </div>
              </div>

              {/* Pipeline 4: Landsat → Cloud Masking */}
              <div
                onClick={() => window.location.href = '/pipelines?id=landsat-cloud'}
                style={{
                  padding: '1rem',
                  background: 'rgba(59, 130, 246, 0.08)',
                  border: '1px solid rgba(59, 130, 246, 0.25)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.12)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.08)'}
              >
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <div style={{flex: 1}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                      <div>
                        <div style={{fontSize: '0.9rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.25rem'}}>
                          Landsat-8 → Cloud Masking
                        </div>
                        <div style={{fontSize: '0.7rem', color: '#888'}}>
                          QA Processing • 📥 24 scenes → ⚙️ Fmask → ✅ Cloud-free
                        </div>
                      </div>
                      <div style={{padding: '0.25rem 0.6rem', background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.4)', borderRadius: '4px', fontSize: '0.65rem', fontWeight: '600', color: '#3b82f6'}}>
                        COMPLETED
                      </div>
                    </div>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', gap: '2rem', fontSize: '0.75rem', paddingLeft: '1.5rem'}}>
                    <div>
                      <span style={{color: '#888'}}>Runtime: </span>
                      <span style={{fontWeight: '600'}}>18 min</span>
                    </div>
                    <div style={{color: '#10b981', fontWeight: '600'}}>Success ✓</div>
                  </div>
                </div>
              </div>

              {/* Pipeline 5: DEM → Terrain Analysis */}
              <div
                onClick={() => window.location.href = '/pipelines?id=dem-terrain'}
                style={{
                  padding: '1rem',
                  background: 'rgba(59, 130, 246, 0.08)',
                  border: '1px solid rgba(59, 130, 246, 0.25)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.12)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.08)'}
              >
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <div style={{flex: 1}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                      <div>
                        <div style={{fontSize: '0.9rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.25rem'}}>
                          Copernicus DEM → Terrain Analysis
                        </div>
                        <div style={{fontSize: '0.7rem', color: '#888'}}>
                          Slope & Aspect • 📥 DEM 30m → ⚙️ GDAL → ✅ 3 layers
                        </div>
                      </div>
                      <div style={{padding: '0.25rem 0.6rem', background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.4)', borderRadius: '4px', fontSize: '0.65rem', fontWeight: '600', color: '#3b82f6'}}>
                        COMPLETED
                      </div>
                    </div>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', gap: '2rem', fontSize: '0.75rem', paddingLeft: '1.5rem'}}>
                    <div>
                      <span style={{color: '#888'}}>Runtime: </span>
                      <span style={{fontWeight: '600'}}>5 min</span>
                    </div>
                    <div style={{color: '#10b981', fontWeight: '600'}}>Success ✓</div>
                  </div>
                </div>
              </div>

              {/* Pipeline 6: Custom ML Model */}
              <div
                onClick={() => window.location.href = '/pipelines?id=crop-yield-ml'}
                style={{
                  padding: '1rem',
                  background: 'rgba(239, 68, 68, 0.08)',
                  border: '1px solid rgba(239, 68, 68, 0.25)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.12)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.08)'}
              >
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <div style={{flex: 1}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                      <div>
                        <div style={{fontSize: '0.9rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.25rem'}}>
                          Multi-Source → Crop Yield ML
                        </div>
                        <div style={{fontSize: '0.7rem', color: '#888'}}>
                          Maize Prediction • 📥 S2 NDVI + Weather → 🤖 Random Forest → ❌ Error
                        </div>
                      </div>
                      <div style={{padding: '0.25rem 0.6rem', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.4)', borderRadius: '4px', fontSize: '0.65rem', fontWeight: '600', color: '#ef4444'}}>
                        FAILED
                      </div>
                    </div>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', gap: '2rem', fontSize: '0.75rem', paddingLeft: '1.5rem'}}>
                    <div>
                      <span style={{color: '#888'}}>Runtime: </span>
                      <span style={{fontWeight: '600'}}>22 min</span>
                    </div>
                    <div style={{color: '#ef4444', fontWeight: '600'}}>Retry available</div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Problems Widget */}
        <div className="compact-widget problems-widget" id="card-problems">
          <div className="widget-header">
            <h3>Data & Processing Errors</h3>
          </div>
          <div className="widget-content">
            <div className="large-number error">
              {liveData.problems}
              <span className="denominator">/{liveData.problems + 5}</span>
            </div>
            <div className="problem-breakdown">
              <div className="breakdown-item" onClick={() => onNavigateToLogs && onNavigateToLogs('rasterio')} style={{cursor: 'pointer'}} title="Click to view RasterIO error logs">
                <span className="breakdown-label">
                  <span style={{color: '#ef4444', fontWeight: 600, marginRight: '0.5rem'}}>BLOCKING</span>
                  RasterIO r/w errors (2)
                  <span style={{color: '#888', fontSize: '0.7rem', marginLeft: '0.5rem'}}>• 5min ago</span>
                </span>
              </div>
              <div className="breakdown-item" onClick={() => onNavigateToLogs && onNavigateToLogs('gdal')} style={{cursor: 'pointer'}} title="Click to view GDAL error logs">
                <span className="breakdown-label">
                  <span style={{color: '#ef4444', fontWeight: 600, marginRight: '0.5rem'}}>BLOCKING</span>
                  GDAL projection failure (1)
                  <span style={{color: '#888', fontSize: '0.7rem', marginLeft: '0.5rem'}}>• 12min ago</span>
                </span>
              </div>
              <div className="breakdown-item" onClick={() => onNavigateToLogs && onNavigateToLogs('hdf5')} style={{cursor: 'pointer'}} title="Click to view HDF5 error logs">
                <span className="breakdown-label">
                  <span style={{color: '#f59e0b', fontWeight: 600, marginRight: '0.5rem'}}>MINOR</span>
                  HDF5 dataset missing (h5py) (2)
                  <span style={{color: '#888', fontSize: '0.7rem', marginLeft: '0.5rem'}}>• 18min ago</span>
                </span>
              </div>
              <div className="breakdown-item" onClick={() => onNavigateToLogs && onNavigateToLogs('numpy')} style={{cursor: 'pointer'}} title="Click to view NumPy error logs">
                <span className="breakdown-label">
                  <span style={{color: '#f59e0b', fontWeight: 600, marginRight: '0.5rem'}}>MINOR</span>
                  NumPy array shape mismatch (1)
                  <span style={{color: '#888', fontSize: '0.7rem', marginLeft: '0.5rem'}}>• 23min ago</span>
                </span>
              </div>
              <div className="breakdown-item" onClick={() => onNavigateToLogs && onNavigateToLogs('xarray')} style={{cursor: 'pointer'}} title="Click to view Xarray error logs">
                <span className="breakdown-label">
                  <span style={{color: '#f59e0b', fontWeight: 600, marginRight: '0.5rem'}}>MINOR</span>
                  Xarray chunking error (1)
                  <span style={{color: '#888', fontSize: '0.7rem', marginLeft: '0.5rem'}}>• 35min ago</span>
                </span>
              </div>
              <div className="breakdown-item" onClick={() => onNavigateToLogs && onNavigateToLogs('pandas')} style={{cursor: 'pointer'}} title="Click to view Pandas error logs">
                <span className="breakdown-label">
                  <span style={{color: '#f59e0b', fontWeight: 600, marginRight: '0.5rem'}}>MINOR</span>
                  Pandas dataframe merge failure (1)
                  <span style={{color: '#888', fontSize: '0.7rem', marginLeft: '0.5rem'}}>• 42min ago</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Stack Availability Widget */}
        <div className="compact-widget availability-widget" id="card-stack-availability">
          <div className="widget-header">
            <h3>EO/GIS Service Health</h3>
          </div>
          <div className="widget-content">
            <div className="availability-grid" title="Ingestion Services, Processing Workers, WMS/WFS APIs, Database Connections">
              {availabilityData.map((item) => (
                <div
                  key={item.id}
                  className={`availability-dot ${item.status}`}
                  title={item.status === 'healthy' ? 'Service Healthy' : 'Service Degraded'}
                ></div>
              ))}
            </div>
            <div className="large-number success">
              {liveData.availability}
            </div>
            <div className="service-breakdown">
              <div style={{fontSize: '0.75rem', color: '#888', marginTop: '0.5rem'}}>
                Ingestion • Workers • APIs • DB
              </div>
              <div style={{fontSize: '0.8rem', marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.35rem'}}>
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.4rem'}}>
                    <span style={{color: '#10b981'}}>✅</span>
                    <span>GeoServer (WMS/WFS)</span>
                  </div>
                  <span style={{color: '#10b981', fontWeight: 600, fontFamily: 'monospace'}}>99.8%</span>
                </div>
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.4rem'}}>
                    <span style={{color: '#10b981'}}>✅</span>
                    <span>PostGIS Database</span>
                  </div>
                  <span style={{color: '#10b981', fontWeight: 600, fontFamily: 'monospace'}}>99.9%</span>
                </div>
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.4rem'}}>
                    <span style={{color: '#f59e0b'}}>⚠️</span>
                    <span>STAC API (slow)</span>
                  </div>
                  <span style={{color: '#f59e0b', fontWeight: 600, fontFamily: 'monospace'}}>96.2%</span>
                </div>
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.4rem'}}>
                    <span style={{color: '#10b981'}}>✅</span>
                    <span>Ingestion Workers</span>
                  </div>
                  <span style={{color: '#10b981', fontWeight: 600, fontFamily: 'monospace'}}>98.5%</span>
                </div>
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.4rem'}}>
                    <span style={{color: '#ef4444'}}>🔴</span>
                    <span>Processing Pipelines</span>
                  </div>
                  <span style={{color: '#ef4444', fontWeight: 600, fontFamily: 'monospace'}}>89.7%</span>
                </div>
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.4rem'}}>
                    <span style={{color: '#10b981'}}>✅</span>
                    <span>Tile Cache (GeoWebCache)</span>
                  </div>
                  <span style={{color: '#10b981', fontWeight: 600, fontFamily: 'monospace'}}>99.5%</span>
                </div>
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.4rem'}}>
                    <span style={{color: '#10b981'}}>✅</span>
                    <span>Auth Service (Keycloak)</span>
                  </div>
                  <span style={{color: '#10b981', fontWeight: 600, fontFamily: 'monospace'}}>100%</span>
                </div>
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.4rem'}}>
                    <span style={{color: '#f59e0b'}}>⚠️</span>
                    <span>THREDDS NetCDF Server</span>
                  </div>
                  <span style={{color: '#f59e0b', fontWeight: 600, fontFamily: 'monospace'}}>94.1%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Geospatial Files */}
        <div className="compact-widget cloud-widget" id="card-geospatial-files">
          <div className="widget-header">
            <h3>Data Storage & File Processing</h3>
          </div>
          <div className="widget-content">
            <div className="cloud-stats">
              <div className="cloud-section">
                <div className="section-title">Storage Overview</div>
                <div className="aws-metric">
                  <span className="metric-number success">{liveData.eoInstances?.total || 0}</span>
                  <span className="metric-unit">total files</span>
                  <div className="metric-detail">
                    <span className="running-count">{liveData.eoInstances?.processing || 0}</span>
                    <span className="running-label">pending: S2(3) L8(2) MODIS(1)</span>
                  </div>
                  <div className="metric-detail" style={{marginTop: '0.5rem'}}>
                    <span className="running-count" style={{color: '#ef4444'}}>2</span>
                    <span className="running-label">failed (corrupt/incomplete)</span>
                  </div>
                </div>
              </div>
              <div className="technologies-section">
                <div className="section-title">File Formats & Storage</div>
                <div className="tech-icons" style={{display: 'flex', flexDirection: 'column', gap: '0.4rem'}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                    <FontAwesomeIcon icon={faFileCode} title="Raster: GeoTIFF & COG files" />
                    <span style={{fontSize: '0.7rem', color: '#888'}}>Raster: 425 GB</span>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                    <FontAwesomeIcon icon={faDatabase} title="NetCDF: Climate & atmospheric data" />
                    <span style={{fontSize: '0.7rem', color: '#888'}}>NetCDF: 210 GB</span>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                    <FontAwesomeIcon icon={faHdd} title="HDF5: MODIS & satellite products" />
                    <span style={{fontSize: '0.7rem', color: '#888'}}>HDF5: 145 GB</span>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                    <FontAwesomeIcon icon={faGlobe} title="GRIB2: Weather forecast data" />
                    <span style={{fontSize: '0.7rem', color: '#888'}}>GRIB2: 52 GB</span>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                    <FontAwesomeIcon icon={faFileCode} title="Vector: Shapefiles & GeoJSON" style={{color: '#10b981'}} />
                    <span style={{fontSize: '0.7rem', color: '#888'}}>Vector: 15 GB</span>
                  </div>
                </div>
              </div>
            </div>
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '1rem', width: '100%', position: 'relative'}}>
              <svg width="120" height="120" viewBox="0 0 100 100">
                {/* Raster: 425/847 = 50.2% */}
                <circle
                  cx="50" cy="50" r="40" fill="none" stroke="#3b82f6" strokeWidth="20"
                  strokeDasharray="125.6 251.2" transform="rotate(-90 50 50)"
                  onMouseEnter={() => setHoveredSegment({ name: 'Raster', desc: 'GeoTIFF & COG files', size: '425 GB', percent: '50.2%', color: '#3b82f6' })}
                  onMouseLeave={() => setHoveredSegment(null)}
                  style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                  opacity={hoveredSegment && hoveredSegment.name !== 'Raster' ? 0.3 : 1}
                />
                {/* NetCDF: 210/847 = 24.8% starting at 50.2% */}
                <circle
                  cx="50" cy="50" r="40" fill="none" stroke="#06b6d4" strokeWidth="20"
                  strokeDasharray="62.2 251.2" strokeDashoffset="-125.6" transform="rotate(-90 50 50)"
                  onMouseEnter={() => setHoveredSegment({ name: 'NetCDF', desc: 'Climate & atmospheric data', size: '210 GB', percent: '24.8%', color: '#06b6d4' })}
                  onMouseLeave={() => setHoveredSegment(null)}
                  style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                  opacity={hoveredSegment && hoveredSegment.name !== 'NetCDF' ? 0.3 : 1}
                />
                {/* HDF5: 145/847 = 17.1% starting at 75% */}
                <circle
                  cx="50" cy="50" r="40" fill="none" stroke="#10b981" strokeWidth="20"
                  strokeDasharray="42.9 251.2" strokeDashoffset="-187.8" transform="rotate(-90 50 50)"
                  onMouseEnter={() => setHoveredSegment({ name: 'HDF5', desc: 'MODIS & satellite products', size: '145 GB', percent: '17.1%', color: '#10b981' })}
                  onMouseLeave={() => setHoveredSegment(null)}
                  style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                  opacity={hoveredSegment && hoveredSegment.name !== 'HDF5' ? 0.3 : 1}
                />
                {/* GRIB2: 52/847 = 6.1% starting at 92.1% */}
                <circle
                  cx="50" cy="50" r="40" fill="none" stroke="#f59e0b" strokeWidth="20"
                  strokeDasharray="15.3 251.2" strokeDashoffset="-230.7" transform="rotate(-90 50 50)"
                  onMouseEnter={() => setHoveredSegment({ name: 'GRIB2', desc: 'Weather forecast data', size: '52 GB', percent: '6.1%', color: '#f59e0b' })}
                  onMouseLeave={() => setHoveredSegment(null)}
                  style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                  opacity={hoveredSegment && hoveredSegment.name !== 'GRIB2' ? 0.3 : 1}
                />
                {/* Vector: 15/847 = 1.8% starting at 98.2% */}
                <circle
                  cx="50" cy="50" r="40" fill="none" stroke="#8b5cf6" strokeWidth="20"
                  strokeDasharray="4.5 251.2" strokeDashoffset="-246" transform="rotate(-90 50 50)"
                  onMouseEnter={() => setHoveredSegment({ name: 'Vector', desc: 'Shapefiles & GeoJSON', size: '15 GB', percent: '1.8%', color: '#8b5cf6' })}
                  onMouseLeave={() => setHoveredSegment(null)}
                  style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                  opacity={hoveredSegment && hoveredSegment.name !== 'Vector' ? 0.3 : 1}
                />
              </svg>
              {hoveredSegment && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  background: 'rgba(0, 0, 0, 0.95)',
                  border: `2px solid ${hoveredSegment.color}`,
                  borderRadius: '8px',
                  padding: '0.75rem 1rem',
                  pointerEvents: 'none',
                  zIndex: 1000,
                  minWidth: '180px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)'
                }}>
                  <div style={{
                    fontWeight: '700',
                    color: hoveredSegment.color,
                    fontSize: '1rem',
                    marginBottom: '0.25rem'
                  }}>
                    {hoveredSegment.name}
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#94a3b8',
                    marginBottom: '0.5rem'
                  }}>
                    {hoveredSegment.desc}
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.85rem',
                    fontWeight: '600'
                  }}>
                    <span style={{ color: '#ffffff' }}>{hoveredSegment.size}</span>
                    <span style={{ color: hoveredSegment.color }}>{hoveredSegment.percent}</span>
                  </div>
                </div>
              )}
              <span className="more-count" style={{fontWeight: '600', marginTop: '0.5rem'}}>847 GB total</span>
            </div>
          </div>
        </div>

        {/* Smartscape */}
        <div className="compact-widget smartscape-widget" id="card-smartscape">
          <div className="widget-header">
            <h3>PostGIS Performance & Indexing</h3>
          </div>
          <div className="widget-content">
            <div className="smartscape-icon">
              <div className="smartscape-lines"></div>
            </div>
            <div className="large-number info">
              {liveData.smartscape}
            </div>
            <div className="smartscape-label">
              <FontAwesomeIcon icon={faDatabase} style={{ marginRight: '0.25rem' }} />
              PostGIS Databases
            </div>
            <div className="postgis-metrics">
              <div className="postgis-stat">
                <span className="postgis-label">Spatial Indexes</span>
                <span className="postgis-value success">847</span>
              </div>
              <div className="postgis-stat" onClick={() => window.location.href = '/metrics#slow-gis-queries'} style={{cursor: 'pointer'}} title="Click to view slow GIS queries details">
                <span className="postgis-label">Slow Queries (ST_*)</span>
                <span className="postgis-value warning">12</span>
              </div>
              <div className="postgis-stat">
                <span className="postgis-label">Raster/Vector/Mixed</span>
                <span className="postgis-value" style={{fontSize: '0.7rem'}}>45/38/17%</span>
              </div>
            </div>
          </div>
        </div>

        {/* External Data Sources Status */}
        <div className="compact-widget external-sources-widget" id="card-external-sources" title="Click on a source to view detailed status">
          <div className="widget-header">
            <h3>External Data Sources Status</h3>
          </div>
          <div className="widget-content">
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
              <div onClick={() => window.location.href = '/external-sources?id=1'} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '4px', cursor: 'pointer', transition: 'all 0.2s'}} onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.2)'} onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.1)'}>
                <span style={{fontSize: '0.8rem'}}>Copernicus SciHub</span>
                <span style={{color: '#10b981', fontSize: '0.75rem'}}>✓ Online</span>
              </div>
              <div onClick={() => window.location.href = '/external-sources?id=2'} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '4px', cursor: 'pointer', transition: 'all 0.2s'}} onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.2)'} onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.1)'}>
                <span style={{fontSize: '0.8rem'}}>ESA Sentinel Hub</span>
                <span style={{color: '#10b981', fontSize: '0.75rem'}}>✓ Online</span>
              </div>
              <div onClick={() => window.location.href = '/external-sources?id=3'} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '4px', cursor: 'pointer', transition: 'all 0.2s'}} onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.2)'} onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.1)'}>
                <span style={{fontSize: '0.8rem'}}>NASA EarthData</span>
                <span style={{color: '#10b981', fontSize: '0.75rem'}}>✓ Online</span>
              </div>
              <div onClick={() => window.location.href = '/external-sources?id=4'} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '4px', cursor: 'pointer', transition: 'all 0.2s'}} onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(245, 158, 11, 0.2)'} onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(245, 158, 11, 0.1)'}>
                <span style={{fontSize: '0.8rem'}}>USGS EarthExplorer</span>
                <span style={{color: '#f59e0b', fontSize: '0.75rem'}}>⚠ Degraded</span>
              </div>
            </div>
            <div style={{marginTop: '1rem', fontSize: '0.65rem', color: '#888', textAlign: 'center'}}>
              Last checked: 2 min ago
            </div>
          </div>
        </div>

        {/* Real User Monitoring */}
        <div className="compact-widget monitoring-widget" id="card-real-user-monitoring" onClick={() => window.location.href = '/web-services'} style={{cursor: 'pointer'}} title="Click to view web services details">
          <div className="widget-header">
            <h3>Web Services Layers</h3>
            <div className="widget-subtitle">
              <FontAwesomeIcon icon={faSatellite} style={{ marginRight: '0.25rem' }} />
              <span className="app-name">GeoPortal</span>
              <span className="app-status">p95: 285ms • 2.3% failed</span>
            </div>
          </div>
          <div className="widget-content">
            <div className="layer-usage-list">
              <div className="layer-usage-item">
                <span className="layer-name">Sentinel-2 True Color</span>
                <span className="usage-count">2.4K views • 180ms</span>
              </div>
              <div className="layer-usage-item">
                <span className="layer-name">Landsat 8 NDVI</span>
                <span className="usage-count">1.8K views • 220ms</span>
              </div>
              <div className="layer-usage-item">
                <span className="layer-name">MODIS Land Surface Temp</span>
                <span className="usage-count">1.2K views • 145ms</span>
              </div>
              <div className="layer-usage-item">
                <span className="layer-name">Copernicus DEM</span>
                <span className="usage-count">890 views • 95ms</span>
              </div>
              <div className="layer-usage-item">
                <span className="layer-name">NDWI (Water Index)</span>
                <span className="usage-count">760 views • 210ms</span>
              </div>
              <div className="layer-usage-item">
                <span className="layer-name">Biomass Map (seasonal)</span>
                <span className="usage-count">540 views • 250ms</span>
              </div>
              <div className="layer-usage-item">
                <span className="layer-name">Yield Estimation (maize)</span>
                <span className="usage-count">420 views • 340ms, 5 errors</span>
              </div>
              <div className="layer-usage-item" style={{marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #333'}}>
                <span className="layer-name" style={{fontWeight: '600'}}>Top AOIs:</span>
              </div>
              <div className="layer-usage-item">
                <span className="layer-name">Mediterranean</span>
                <span className="usage-count" style={{color: '#888'}}>1.1K queries</span>
              </div>
              <div className="layer-usage-item">
                <span className="layer-name">Danube Basin</span>
                <span className="usage-count" style={{color: '#888'}}>820 queries</span>
              </div>
              <div className="layer-usage-item">
                <span className="layer-name">Thessaly Plain</span>
                <span className="usage-count" style={{color: '#888'}}>600 queries</span>
              </div>
            </div>
          </div>
        </div>

        {/* Services Chart */}
        <div className="compact-widget services-widget" id="card-services">
          <div className="widget-header">
            <h3>Mission Data Ingestion</h3>
          </div>
          <div className="widget-content">
            <div className="services-stats" style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem'}}>
              <div className="service-stat" style={{textAlign: 'left'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem'}}>
                  <span className="stat-label" style={{fontSize: '0.75rem'}}>Sentinel-2</span>
                  <span style={{color: '#ef4444', fontSize: '0.65rem'}}>↓ -15%</span>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline'}}>
                  <span className="stat-number" style={{fontSize: '1.3rem'}}>18</span>
                  <span style={{fontSize: '0.7rem', color: '#888'}}>scenes/h</span>
                </div>
              </div>
              <div className="service-stat" style={{textAlign: 'left'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem'}}>
                  <span className="stat-label" style={{fontSize: '0.75rem'}}>Landsat-8</span>
                  <span style={{color: '#10b981', fontSize: '0.65rem'}}>↑ +8%</span>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline'}}>
                  <span className="stat-number" style={{fontSize: '1.3rem'}}>12</span>
                  <span style={{fontSize: '0.7rem', color: '#888'}}>scenes/h</span>
                </div>
              </div>
              <div className="service-stat" style={{textAlign: 'left'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem'}}>
                  <span className="stat-label" style={{fontSize: '0.75rem'}}>MODIS</span>
                  <span style={{color: '#10b981', fontSize: '0.65rem'}}>↑ +3%</span>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline'}}>
                  <span className="stat-number" style={{fontSize: '1.3rem'}}>8</span>
                  <span style={{fontSize: '0.7rem', color: '#888'}}>tiles/h</span>
                </div>
              </div>
              <div className="service-stat" style={{textAlign: 'left'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem'}}>
                  <span className="stat-label" style={{fontSize: '0.75rem'}}>Sentinel-1</span>
                  <span style={{color: '#888', fontSize: '0.65rem'}}>stable</span>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline'}}>
                  <span className="stat-number" style={{fontSize: '1.3rem'}}>6</span>
                  <span style={{fontSize: '0.7rem', color: '#888'}}>scenes/h</span>
                </div>
              </div>
            </div>

            <div className="services-chart" style={{marginBottom: '0.75rem'}}>
              <ResponsiveContainer width="100%" height={50}>
                <BarChart data={servicesChartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                  <Bar dataKey="sentinel" fill="#3b82f6" />
                  <Bar dataKey="landsat" fill="#8b5cf6" />
                  <Bar dataKey="modis" fill="#10b981" />
                  <Bar dataKey="copernicus" fill="#f59e0b" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div style={{paddingTop: '0.75rem', borderTop: '1px solid rgba(51, 65, 85, 0.5)'}}>
              <div style={{fontSize: '0.7rem', color: '#94a3b8', marginBottom: '0.4rem'}}>Recent Issues:</div>
              <div style={{display: 'flex', flexDirection: 'column', gap: '0.3rem', fontSize: '0.7rem'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <div style={{width: '6px', height: '6px', borderRadius: '50%', background: '#ef4444'}}></div>
                  <span style={{color: '#cbd5e1'}}>S2 missing bands (2)</span>
                </div>
                <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <div style={{width: '6px', height: '6px', borderRadius: '50%', background: '#f59e0b'}}></div>
                  <span style={{color: '#cbd5e1'}}>L8 corrupted metadata (1)</span>
                </div>
                <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <div style={{width: '6px', height: '6px', borderRadius: '50%', background: '#f59e0b'}}></div>
                  <span style={{color: '#cbd5e1'}}>MODIS missing variable (1)</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Docker Widget */}
        <div className="compact-widget docker-widget" id="card-docker">
          <div className="widget-header">
            <h3>Processing Containers Health</h3>
          </div>
          <div className="widget-content">
            <div className="docker-stats">
              <div className="docker-main">
                <div className="docker-number">{liveData.dockerContainers}</div>
                <div className="docker-label">Active EO/GIS containers</div>
                <div className="docker-time">
                  <span>last Thu</span>
                  <span className="time-now">now</span>
                </div>
              </div>
              <div className="docker-details">
                <div className="docker-row">
                  <span className="label">Running</span>
                  <div className="progress-mini">
                    <div className="progress-fill" style={{ width: '85%', backgroundColor: '#10b981' }}></div>
                  </div>
                  <span className="value">{liveData.dockerContainers}</span>
                </div>
                <div className="docker-row">
                  <span className="label">Crash loops</span>
                  <div className="progress-mini">
                    <div className="progress-fill" style={{ width: '5%', backgroundColor: '#ef4444' }}></div>
                  </div>
                  <span className="value" style={{ color: '#ef4444' }}>2</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hosts Widget */}
        <div className="compact-widget hosts-widget" id="card-hosts">
          <div className="widget-header">
            <h3>Compute Resources</h3>
          </div>
          <div className="widget-content">
            <div className="large-number primary">
              {liveData.hosts}
            </div>
            <div className="hosts-label">Processing nodes</div>
            <div className="hosts-metrics" style={{marginTop: '1rem', display: 'flex', gap: '1rem', fontSize: '0.85rem'}}>
              <div>
                <div style={{color: '#10b981'}}>CPU: 67%</div>
                <div style={{color: '#888'}}>GPU: 45%</div>
              </div>
              <div>
                <div style={{color: '#ef4444'}}>Disk I/O: 89%</div>
                <div style={{color: '#888'}}>Memory: 72%</div>
              </div>
            </div>
          </div>
        </div>

        {/* Database Widget */}
        <div className="compact-widget database-widget" id="card-database">
          <div className="widget-header">
            <h3>Database Workload</h3>
          </div>
          <div className="widget-content">
            <div className="database-stats">
              <div className="db-stat">
                <div className="db-number">{liveData.geoDatabase?.queries || 0}</div>
                <div className="db-unit">tx/sec</div>
                <div className="db-label">Transactions</div>
              </div>
              <div className="db-stat">
                <div className="db-number">{liveData.geoDatabase?.responseTime || 0}</div>
                <div className="db-unit">ms</div>
                <div className="db-label">Response Time</div>
              </div>
              <div className="db-stat">
                <div className="db-number" style={{fontSize: '1.2rem'}}>65/35</div>
                <div className="db-unit">%</div>
                <div className="db-label">Raster/Vector</div>
              </div>
            </div>
          </div>
        </div>

        {/* Network Status */}
        <div className="compact-widget network-widget" id="card-network-status">
          <div className="widget-header">
            <h3>Data Transfer & Client Load</h3>
          </div>
          <div className="widget-content">
            <div className="network-stats">
              <div className="network-row">
                <span className="network-label">Bandwidth</span>
                <span className="network-value">{liveData.networkVolume}</span>
                <span className="network-unit">MB/s</span>
              </div>
              <div className="network-processes">
                <span className="process-error">{liveData.networkProcesses}</span>
                <span className="process-label">Active clients (WMS/QGIS/API)</span>
              </div>
              <div className="network-processes" style={{marginTop: '0.5rem'}}>
                <span className="process-hosts" style={{color: '#ef4444'}}>3</span>
                <span className="process-label">Failed transfers/timeouts</span>
              </div>
            </div>
            <div className="network-chart">
              <ResponsiveContainer width="100%" height={60}>
                <LineChart data={networkChartData.slice(-20)} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={false}
                    fill="#3b82f6"
                    fillOpacity={0.3}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

      </div>

      {/* AI Analysis Modal */}
      {showAIModal && (
        <div className="ai-modal-overlay" onClick={() => setShowAIModal(false)}>
          <div className="ai-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="ai-modal-header">
              <h3>
                <FontAwesomeIcon icon={faRobot} style={{ marginRight: '0.5rem', color: '#3b82f6' }} />
                AI System Analysis
              </h3>
              <button className="ai-modal-close" onClick={() => setShowAIModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="ai-modal-body">
              <div className="ai-text-content">
                {aiText.split('\n').map((line, index) => {
                  // Parse markdown-style formatting
                  let formattedLine = line;

                  // Bold: **text** -> <strong>text</strong>
                  formattedLine = formattedLine.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

                  // Headers: ### text -> larger text
                  const isHeader = line.match(/^(#{1,3})\s+(.+)$/);
                  if (isHeader) {
                    const level = isHeader[1].length;
                    const text = isHeader[2];
                    return (
                      <h4 key={index} style={{
                        fontSize: level === 1 ? '1.1rem' : level === 2 ? '1rem' : '0.95rem',
                        fontWeight: 'bold',
                        marginTop: level === 1 ? '1rem' : '0.8rem',
                        marginBottom: '0.4rem',
                        color: '#3b82f6'
                      }}>
                        {text}
                      </h4>
                    );
                  }

                  return (
                    <p key={index} dangerouslySetInnerHTML={{ __html: formattedLine }} />
                  );
                })}
                {isStreaming && <span className="ai-cursor">▊</span>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompactOverview;